import { take } from "lodash";
import { useMemo, useState } from "react";
import { AssessmentType } from "types/graphql-schema";

import AssessmentQuestionHeading from "@apps/assessments/components/questions/assessment-question-heading";
import { getQuestionClass } from "@apps/assessments/components/questions/question";
import {
  AssessmentAnswerWithResponderAndId,
  AssessmentQuestion,
} from "@apps/assessments/components/questions/types";
import useLabel from "@apps/use-label/use-label";
import Button, { buttonTheme } from "@components/button/button";
import ToggleChevronIcon from "@components/layout/toggle-chevron-icon";
import { isEmptyValue } from "@components/wysiwyg/helpers";
import { pluralize } from "@helpers/string";

const MAX_COMMENT_CHUNK_SIZE = 10;

const EngagementSurveyDeliveryQuestion = ({
  answers,
  question,
}: {
  answers: AssessmentAnswerWithResponderAndId[];
  question: AssessmentQuestion;
}) => {
  const label = useLabel();
  const [commentLimit, setCommentLimit] = useState(MAX_COMMENT_CHUNK_SIZE);
  const [collapsedComments, setCollapsedComments] = useState(true);
  const QuestionClass = getQuestionClass(question, { label });

  const answersWithComment = useMemo(
    () => answers.filter((answer) => !isEmptyValue(answer.comment)),
    [answers]
  );

  const commentContent = useMemo(
    () =>
      QuestionClass.getDeliveryComments({
        answers: take(answersWithComment, commentLimit),
      }),
    [QuestionClass, answersWithComment, commentLimit]
  );

  if (answers.length === 0) {
    return null;
  }

  return (
    <div
      key={question.id}
      className="border rounded-lg"
      aria-label={`Engagement survey delivery question: ${question.title}`}
    >
      <div className="px-6 pt-6 flex items-baseline justify-between">
        <div>
          <AssessmentQuestionHeading
            question={question}
            assessmentType={AssessmentType.EngagementSurvey}
          />
        </div>
        <div className="text-sm text-gray-500">
          {answers.length} {pluralize("responder", answers.length)}
        </div>
      </div>

      <div className="mt-4 px-6 pb-6">
        {QuestionClass.getEngagementSurveyDelivery({ answers })}
      </div>

      {commentContent && (
        <div className="border-t p-6 flex flex-col gap-4">
          <div
            className="px-2 py-1 -mx-2 rounded hover:bg-gray-50"
            role="button"
            onClick={() => setCollapsedComments(!collapsedComments)}
          >
            <div className="flex items-center gap-2 font-semibold">
              <div className="text-sm text-gray-900">Comments</div>
              <div className="text-xs py-0.5 px-2 bg-gray-100 rounded-md">
                {answersWithComment.length}
              </div>
              <ToggleChevronIcon expanded={!collapsedComments} />
            </div>
          </div>
          {!collapsedComments && commentContent}
          {!collapsedComments && commentLimit < answersWithComment.length && (
            <div className="mt-6">
              <Button
                theme={buttonTheme.lightGray}
                onClick={() =>
                  setCommentLimit(commentLimit + MAX_COMMENT_CHUNK_SIZE)
                }
                text="View More"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EngagementSurveyDeliveryQuestion;
